import React, { useState, useEffect } from 'react';
import { RouteComponentProps, Link } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Breadcrumbs,
  Paper,
  Typography,
  Grid,
  IconButton,
  Card,
  CardContent,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import prism from 'prismjs';
import {
  CheckCircle,
  GetApp,
  Attachment as AttachmentIcon,
  OpenInBrowser,
  Save as SaveIcon,
  Feedback as FeedbackIcon,
} from '@material-ui/icons';
import Dropzone, { DropzoneState } from 'react-dropzone';
import path from 'path';
import JSZip from 'jszip';

import StudentLayout from '../../../../../../layouts/StudentLayout';
import auth from '../../../../../../libs/auth';
import Spec from '../../../../../../models/Spec';
import APITools from '../../../../../../models/APITools';
import useZipFileExplorerPopUp from '../../../../../../hooks/useZipFileExplorerPopUp';
import CourseWork from '../../../../../../models/CourseWork';
import StudentSubmission, {
  DriveFile,
  MoodleFile,
} from '../../../../../../models/StudentSubmission';
import {
  SpecTest,
  TestResult,
} from '../../../../../../components/Spec/SpecTest';
import { useDialog } from '../../../../../../context/Dialog';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import utils from '../../../../../../libs/utils';
import Course from '../../../../../../models/Course';
import { ClassroomResourceLink } from '../../../../../../components/Links';
import useSpecComments from '../../../../../../hooks/useSpecComments';
import useLoadingStatusMap from '../../../../../../hooks/useLoadingStatusMap';
import { TitleHeader } from '../../../../../../components/Headers';
import SpecFeedback from '../../../../../../models/SpecFeedback';
import { ExamCountdown } from '../../../../../../components/ExamCountdown';
import Assignment from '../../../../../../models/Assignment';

const useStyles = makeStyles((theme) => ({
  input: {
    display: 'none',
  },
  fileDropArea: {
    height: '150px',
    backgroundColor: 'rgba(55, 71, 79, 0.1)',
    borderRadius: theme.shape.borderRadius,
    padding: '15px',
    userSelect: 'none',
    outline: 'none',
    cursor: 'pointer',
  },
  fileDropZone: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed currentColor',
    borderRadius: theme.shape.borderRadius,
    opacity: '0.5',
  },
  fileDropZoneDrag: {
    opacity: '1',
  },
  buttonsContainer: {
    display: 'flex',
    width: '400px',
  },
}));

interface WorkItem {
  spec: Spec;
  submission: {
    file: MoodleFile;
    content: string;
  } | null;
  answer: string;
  rawFileAnswer?: File;
  testResult: TestResult | null;
  manual: boolean;
  uploadStatus?: {
    isLoading: boolean;
    errorMessage: string;
    successMessage: string;
  };
}

const sampleCode = `// Example
// Problem: Print sum of two numbers

function main(num1, num2) {
  const sum = num1 + num2;
  console.log(sum);
}
`;

const AssignmentProfile = (
  props: RouteComponentProps<{ course_id: string; assignment_id: string }>
) => {
  const { course_id: courseId, assignment_id: courseWorkId } =
    props.match.params;

  const [course, setCourse] = useState<Course | null>(null);
  const [courseWork, setCourseWork] = useState<CourseWork | null>(null);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [errorMessage, setErrorMessage] = useState('' as string);
  const [overridden, setOverridden] = useState(false as boolean);
  const [grade, setGrade] = useState('' as string);
  const [recalculating, setRecalculating] = useState(false as boolean);
  const [hasExamEnded, setExamEnded] = useState(false as boolean);
  const [isFileRejected, setIsFileRejected] = useState(false as boolean);


  const classes = useStyles();

  const { confirm, alert, prompt } = useDialog();

  const { renderSnackbar, showMessage } = useSnackbar();

  const { renderModal, openFileExplorer } = useZipFileExplorerPopUp();

  const {
    renderModal: renderSpecCommentsModal,
    renderOpenButton: renderSpecCommentsOpenButton,
  } = useSpecComments();

  const {
    isLoading: isFeedbackLoading,
    executeWithStatus: executeFeedbackWithStatus,
  } = useLoadingStatusMap();

  const getGradeStatus = async () => {
    const params = {
      accountID: auth.getSession()?.accountID,
      assignmentID: courseWorkId,
    };
    const queryString = APITools.parseQueryParams(params);
    const { data } = await axios.get(
      `/admin/grade-override-status${queryString}`
    );
    setOverridden(data);
  };

  const loadWorkItems = async () => {
    const session = auth.getSession();
    const specs = await Spec.find({
      assignmentID: courseWorkId,
    });

    // const _rawSubmittedFiles: DriveFile[] = [];
    const _workItems: WorkItem[] = specs.map((spec) => {
      return {
        spec,
        submission: null,
        answer: '',
        testResult: null,
        manual: !spec.data.length,
      };
    });

    setWorkItems(_workItems);

    const submissionResponse = await CourseWork.getSubmission({
      assignmentIds: [courseWorkId],
    });

    const submission = submissionResponse?.data;

    if (submission) {
      const submissions = _workItems.map((item, index) => {
        const matchingSubmission = submission.find(
          (sub: { file: { title: string } }) =>
            sub.file.title.split('.')[0] === item.spec.name
        );

        if (matchingSubmission) {
          const content = matchingSubmission.file.content;
          const title = matchingSubmission.file.title as string;

          if (title.endsWith('.zip')) {
            const binaryString = atob(content);

            const uint8Array = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              uint8Array[i] = binaryString.charCodeAt(i);
            }

            const zipBlob = new Blob([uint8Array], {
              type: 'application/zip',
            });
            const zipFile = new File([zipBlob], item.spec.name + '.zip', {
              type: 'application/zip',
            });

            const rawFileAnswer = zipFile;
            const alternateLink = URL.createObjectURL(zipFile);

            return {
              ...item,
              submission: {
                file: {
                  title,
                  id: '',
                  alternateLink,
                },
                content,
              },
              rawFileAnswer,
            };
          }

          return {
            ...item,
            submission: {
              file: {
                title,
                id: '',
                alternateLink: '',
              },
              content,
            },
            answer: content,
          };
        }

        return item;
      });

      setWorkItems(submissions);
    }
  };

  const loadCourseWork = async () => {
    const session = auth.getSession();

    const moodleCourseAssignments = await CourseWork.getMoodleCourseAssignments(
      {
        token: session?.accessToken,
        courseId,
      }
    );

    if (!moodleCourseAssignments)
      throw new Error('Unable to get coursework profile');

    const _courseWork = moodleCourseAssignments.find(
      (obj) => obj.id === courseWorkId
    ) as CourseWork;
    setCourseWork(_courseWork);
  };

  const loadCourse = async () => {
    const _course = await Course.findById({
      courseId,
    });

    setCourse(_course);
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      await Promise.all([
        loadWorkItems(),
        loadCourseWork(),
        // getGradeStatus(),
        loadCourse(),
      ]);
    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setIsLoading(false);
      window.scrollTo(0, 0);
    }
  };

  const submitWorkItem = async (
    workItem: WorkItem,
    rawFile: File[] | null = null
  ) => {
    const confirmed = await confirm(
      `Are you sure to submit ${
        rawFile && rawFile.length > 1 ? 'these files' : 'this file'
      }?`
    );
    if (!confirmed) return;

    try {
      setWorkItems(
        workItems.map((item) => {
          if (item === workItem)
            item.uploadStatus = {
              isLoading: true,
              errorMessage: '',
              successMessage: '',
            };
          return item;
        })
      );

      const shouldGradeSubmission = workItems.some((item) => !item.manual);

      if (workItems.length >= 2) {
        (async () => {
          const zip = new JSZip();

          await Promise.all(
            workItems.map(async (item, index) => {
              if (workItem === item && rawFile) {
                const rawFileZip = new JSZip();

                rawFile.forEach((rawItem, index) => {
                  const fileBlob = new Blob([rawItem], {
                    type: rawItem.type,
                  });
                  rawFileZip.file(rawItem.name, fileBlob);
                });
                const zipBlob = await rawFileZip.generateAsync({
                  type: 'blob',
                });

                zip.file(`${workItem.spec.name}.zip`, zipBlob);

                const zipFile = new File(
                  [zipBlob],
                  workItem.spec.name + '.zip',
                  {
                    type: 'application/zip',
                  }
                );
                const zipUrl = URL.createObjectURL(zipFile);

                const rawFileAnswer = zipFile;
                const submission = {
                  file: {
                    id: '',
                    title: zipFile.name,
                    alternateLink: zipUrl,
                  },
                  content: workItem.answer,
                };

                setWorkItems(
                  workItems.map((_item) => {
                    if (_item === workItem) {
                      _item.submission = submission;
                      _item.rawFileAnswer = rawFileAnswer;
                    }
                    return _item;
                  })
                );
              } else {
                if (item.manual && item.rawFileAnswer) {
                  const zipBlob = new Blob([item.rawFileAnswer], {
                    type: item.rawFileAnswer.type,
                  });
                  zip.file(`${item.spec.name}.zip`, zipBlob);

                  const zipFile = new File(
                    [zipBlob],
                    workItem.spec.name + '.zip',
                    {
                      type: 'application/zip',
                    }
                  );
                  const zipUrl = URL.createObjectURL(zipFile);

                  const rawFileAnswer = zipFile;
                  const submission = {
                    file: {
                      id: '',
                      title: zipFile.name,
                      alternateLink: zipUrl,
                    },
                    content: workItem.answer,
                  };

                  setWorkItems(
                    workItems.map((_item) => {
                      if (_item === workItem) {
                        _item.submission = submission;
                        _item.rawFileAnswer = rawFileAnswer;
                      }
                      return _item;
                    })
                  );
                } else {
                  const jsBlob = new Blob([item.answer], {
                    type: 'text/javascript',
                  });
                  zip.file(`${item.spec.name}.js`, jsBlob);

                  setWorkItems(
                    workItems.map((_item) => {
                      if (_item === workItem) {
                        _item.submission = {
                          file: {
                            id: '',
                            title: _item.spec.name + '.js',
                            alternateLink:
                              _item.submission?.file.alternateLink ?? '',
                          },
                          content: workItem.answer,
                        };
                      }
                      return _item;
                    })
                  );
                }
              }
            })
          );

          const zipBase64 = await zip.generateAsync({ type: 'base64' });
          try {
            const response = await Assignment.saveMoodleAssignment({
              fileName: 'solution.zip',
              fileContent: zipBase64,
              assignmentId: courseWorkId,
            });

            if (shouldGradeSubmission) {
              await Assignment.saveMoodleAssignmentGrade({
                assignmentId: courseWorkId,
                userId: auth.getSession()?.user?.userid.toString(),
                attemptNumber: -1,
                addAttempt: 0,
                workFlowState: 'graded',
                applyToAll: 0,
                comment: '',
                commentFormat: 1,
                fileManager: 1,
              });
            }

            setWorkItems(
              workItems.map((item) => {
                if (item === workItem) {
                  // item.submission = {
                  //   file: {
                  //     id: '',
                  //     title:
                  //       item.spec.name + (item.rawFileAnswer ? '.zip' : '.js'),
                  //     alternateLink: item.submission?.file.alternateLink ?? '',
                  //   },
                  //   content: workItem.answer,
                  // };
                  item.uploadStatus = {
                    isLoading: false,
                    errorMessage: '',
                    successMessage:
                      response.data.length === 0
                        ? 'Submission Success!'
                        : 'Submission Failed!',
                  };
                }
                return item;
              })
            );

            // await loadWorkItems()

            showMessage('Submission Success!', 'success');
          } catch (error) {
            console.log('ERROR: ', error);
          }
        })();
      } else {
        if (rawFile) {
          (async () => {
            const zip = new JSZip();
            rawFile.forEach((item, index) => {
              const fileBlob = new Blob([item], {
                type: item.type,
              });
              zip.file(item.name, fileBlob);
            });

            const zipBase64 = await zip.generateAsync({ type: 'base64' });

            try {
              const response = await Assignment.saveMoodleAssignment({
                fileName: workItem.spec.name + '.zip',
                fileContent: zipBase64,
                assignmentId: courseWorkId,
              });

              const zipBlob = await zip.generateAsync({ type: 'blob' });
              const zipAnswer = new File(
                [zipBlob],
                workItem.spec.name + '.zip',
                {
                  type: 'application/zip',
                }
              );
              const zipUrl = URL.createObjectURL(zipAnswer);

              setWorkItems(
                workItems.map((item) => {
                  if (item === workItem) {
                    item.submission = {
                      file: {
                        id: '',
                        title: zipAnswer.name,
                        alternateLink: zipUrl,
                      },
                      content: workItem.answer,
                    };
                    item.uploadStatus = {
                      isLoading: false,
                      errorMessage: '',
                      successMessage:
                        response.data.length === 0
                          ? 'Submission Success!'
                          : 'Submission Failed!',
                    };

                    if (rawFile) {
                      item.rawFileAnswer = zipAnswer;
                    }
                  }
                  return item;
                })
              );

              showMessage('Submission Success!', 'success');
            } catch (error) {
              console.log('ERROR: ', error);
            }
          })();
        } else {
          const file = (() => {
            const blobFile: any = new Blob([workItem.answer], {
              type: 'text/javascript',
            });
            return new File([blobFile], `${workItem.spec.name}.js`, {
              lastModified: new Date().getTime(),
            });
          })();

          const reader = new FileReader();

          reader.onload = async () => {
            const base64Content = btoa(reader.result as string);

            try {
              const response = await Assignment.saveMoodleAssignment({
                fileName: file.name,
                fileContent: base64Content,
                assignmentId: courseWorkId,
              });

              if (shouldGradeSubmission) {
                await Assignment.saveMoodleAssignmentGrade({
                  assignmentId: courseWorkId,
                  userId: auth.getSession()?.user?.userid.toString(),
                  attemptNumber: -1,
                  addAttempt: 0,
                  workFlowState: 'graded',
                  applyToAll: 0,
                  comment: '',
                  commentFormat: 1,
                  fileManager: 1,
                });
              }

              setWorkItems(
                workItems.map((item) => {
                  if (item === workItem) {
                    item.submission = {
                      file: {
                        id: '',
                        title: file.name,
                        alternateLink: '',
                      },
                      content: workItem.answer,
                    };
                    item.uploadStatus = {
                      isLoading: false,
                      errorMessage: '',
                      successMessage:
                        response.data.length === 0
                          ? 'Submission Success!'
                          : 'Submission Failed!',
                    };
                  }
                  return item;
                })
              );

              showMessage('Submission Success!', 'success');
            } catch (error) {
              console.log('ERROR: ', error);
            }
          };

          reader.readAsBinaryString(file);
        }
      }
    } catch (error) {
      showMessage(utils.getRequestErrorMessage(error), 'error');
      setWorkItems(
        workItems.map((item) => {
          if (item === workItem)
            item.uploadStatus = {
              isLoading: false,
              errorMessage: error.message,
              successMessage: '',
            };
          return item;
        })
      );
    }
  };

  const validateWorkItemAnswer = async (workItem: WorkItem) => {
    const hasMainFunction = (str: string): boolean => {
      if (/function(\s)+main(\s|)+\(/g.test(str)) return true;
      if (/(var|let|const)(\s|)+main(\s|)+=(\s|)+\(.*\)(\s|)+=>/g.test(str))
        return true;
      return /(var|let|const)(\s|)+\w+(\s|)+=(\s|)+function(\s|)+\(/g.test(str);
    };

    const hasClassImplementation = (str: string): boolean => {
      return /(class)(\s)*(\S)+(\s)*\{/g.test(str);
    };

    try {
      const answer = workItem.answer.trim();
      if (!answer) throw new Error('Please provide an answer to test.');
      if (!hasMainFunction(answer) && !hasClassImplementation(answer))
        throw new Error('Unable to find main function.');
    } catch (error) {
      alert(
        <>
          <p className="mt-0 mb-2">{error.message}</p>
          <p className="mt-0 mb-2">
            <b>Important:</b> Please name your function into "main".
          </p>
          <pre
            dangerouslySetInnerHTML={{
              __html: prism.highlight(
                sampleCode,
                prism.languages.javascript,
                'javascript'
              ),
            }}
          />
        </>,
        {
          title: 'Run Test Validation',
        }
      );
      throw new Error(error);
    }
  };

  const recalculateGrade = () => {
    setRecalculating(true);
    const res = workItems.map(async (workItem) => {
      if (workItem.answer !== '') {
        return await axios.post(
          '/exam/submit',
          JSON.stringify({
            submitterID: utils.generateSubmissionUID(courseId, courseWorkId),
            activityID: workItem.spec.id,
            assignmentID: courseWorkId,
            code: workItem.answer,
            isAdmin: auth.isAdmin(),
            accountID: auth.getSession()?.user?.userid.toString(),
          })
        );
      }
    });
    Promise.all(res).then(async () => {
      const data = (
        await axios.post(
          '/classroom/courses/course-work/recalculate-grade',
          JSON.stringify({
            courseID: courseId,
            personID: auth.getSession()?.user?.userid.toString(),
            assignmentID: courseWorkId,
          })
        )
      ).data;
      if (data) {
        setRecalculating(false);
      }
    });
  };

  const downloadDriveAssignment = async (fileId: string) => {
    const params = { fileId };
    const queryString = APITools.parseQueryParams(params);
    const { data } = await axios.get(
      `/classroom/courses/course-work/student-submission/file${queryString}`
    );
    const { fileBinary, fileType } = data;

    const arr = new Uint8Array(Buffer.from(fileBinary, 'hex'));
    const blob = new Blob([arr], { type: fileType });
    return blob;
  };

  const openFeedbackForm = async (spec: Spec) => {
    const feedback = await prompt(
      <Box>
        <Typography>
          Please enter your feedback for <b>{spec.name}</b>. Your feedback will
          be read by all the training teams at Dev League.
        </Typography>

        <Box pt={2}>
          <Typography>
            Your feedback helps us improve and create better learning
            experiences for you and for our Dev League students.
          </Typography>
        </Box>
      </Box>,
      {
        title: 'Send Feedback',
        fieldProps: {
          placeholder: 'Enter feedback',
          required: true,
          fullWidth: true,
          multiline: true,
          inputProps: {
            style: {
              minHeight: '5em',
            },
          },
        },
      }
    );

    await executeFeedbackWithStatus(
      spec.id,
      async () => {
        if (typeof feedback === 'string') {
          await SpecFeedback.create({
            specId: spec.id,
            description: feedback,
          });

          await alert('Feedback has been sent successfully!', {
            title: 'Feedback sent',
          });
        }
      },
      (e) =>
        alert(utils.getRequestErrorMessage(e), {
          title: 'Send Feedback Error',
        })
    );
  };

  useEffect(() => {
    (async () => await loadData())();
  }, []);

  let examEndedInterval: NodeJS.Timeout;
  useEffect(() => {
    if (courseWork && courseWork.dueDate)
      examEndedInterval = setInterval(() => {
        setExamEnded((_examEnded) => utils.hasExamEnded(courseWork));

        if (utils.hasExamEnded(courseWork)) clearInterval(examEndedInterval);
      }, 1000);
  }, [courseWork]);

  return (
    <StudentLayout>
      <div>
        {renderSnackbar()}
        {renderModal()}
        {renderSpecCommentsModal()}
      </div>

      <div className="py-4">
        <TitleHeader
          breadcrumb={
            <Breadcrumbs aria-label="breadcrumb">
              <Link to={Course.getListUrlPath('student')}>Courses</Link>
              <Typography>{course?.fullname || courseId}</Typography>
              <Link to={CourseWork.getListUrlPath(courseId, 'student')}>
                Assignment
              </Link>
              <Typography>{courseWork?.name || courseWorkId}</Typography>
            </Breadcrumbs>
          }
        >
          <Typography variant="h5">
            <span>{courseWork?.name || courseWorkId} </span>
            {!!courseWork && <ClassroomResourceLink resource={courseWork} />}
          </Typography>
        </TitleHeader>

        {(() => {
          if (isLoading)
            return (
              <div className="text-center p-5">
                <CircularProgress />
                <p className="m-0">Loading Assignment</p>
              </div>
            );

          if (errorMessage)
            return (
              <div className="text-center p-5">
                <h2 className="m-0">Error Loading Assignment</h2>
                <p>{errorMessage}</p>
                <Button
                  type="button"
                  onClick={loadData}
                  color="primary"
                  className="mt-3"
                  variant="contained"
                >
                  Try Again
                </Button>
              </div>
            );

          if (!workItems.length)
            return (
              <div className="text-center p-5">
                <p className="m-0">
                  This exercise does not have an automated test.
                </p>
              </div>
            );

          if (courseWork)
            return (
              <>
                <ExamCountdown courseWork={courseWork} />
                <div className="mb-3">
                  <div className={classes.buttonsContainer}>
                    {/* <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      color="primary"
                      className="mt-2 mb-1"
                      disabled={recalculating || overridden}
                      onClick={recalculateGrade}
                      style={{ marginRight: 15 }}
                    >
                      {recalculating && (
                        <CircularProgress size={14} className="mr-2" />
                      )}
                      <span>Recalculate Grade</span>
                    </Button> */}
                  </div>

                  {!!grade && (
                    <Typography variant="subtitle1" className="mt-2">
                      <CheckCircle color="primary" fontSize="small" /> Grade:{' '}
                      {grade}/{courseWork.maxPoints}
                    </Typography>
                  )}
                </div>

                {workItems.map((workItem, index) => (
                  <Paper
                    key={workItem.spec.id}
                    className="mt-4"
                    style={{ overflow: 'hidden' }}
                  >
                    {/* title of the activity */}
                    <div
                      className="px-3 py-2 d-flex"
                      style={{ backgroundColor: '#37474f', color: 'white' }}
                    >
                      <h3 className="m-0 ">{workItem.spec.name}</h3>
                    </div>

                    <div className="p-3">
                      {(() => {
                        if (workItem.manual)
                          return (
                            <Box>
                              <Grid container spacing={2} direction="row">
                                {workItem.spec.name.toUpperCase() !== 'EXAM' ||
                                (workItem.spec.name.toUpperCase() === 'EXAM' &&
                                  !hasExamEnded) ? (
                                  <Grid
                                    item
                                    container
                                    direction="column"
                                    xs={
                                      workItem.submission &&
                                      workItem.submission.file.alternateLink
                                        ? 6
                                        : 12
                                    }
                                  >
                                    {(() => {
                                      if (workItem.uploadStatus?.isLoading)
                                        return (
                                          <Grid
                                            container
                                            alignItems="center"
                                            justify="center"
                                            className={classes.fileDropArea}
                                          >
                                            <div className="text-center">
                                              <CircularProgress size={25} />
                                              <h6 className="m-0">Uploading</h6>
                                            </div>
                                          </Grid>
                                        );

                                      return (
                                        <Dropzone
                                          onDrop={(
                                            acceptedFiles,
                                            rejectedFiles
                                          ) => {
                                            if (
                                              acceptedFiles.length > 0 &&
                                              rejectedFiles.length === 0
                                            ) {
                                              setIsFileRejected(false);
                                              (async () =>
                                                await submitWorkItem(
                                                  workItem,
                                                  acceptedFiles
                                                ))();
                                            }
                                          }}
                                          onDropRejected={() => {
                                            setIsFileRejected(true);
                                          }}
                                          noClick={false}
                                          validator={(file) => {
                                            if (file.name.endsWith('.zip')) {
                                              return {
                                                code: 'unsupported-file-format',
                                                message:
                                                  '.zip file cannot be accepted',
                                              };
                                            }

                                            return null;
                                          }}
                                        >
                                          {(state: DropzoneState) => {
                                            return (
                                              <div
                                                {...state.getRootProps()}
                                                className={classes.fileDropArea}
                                              >
                                                <div
                                                  className={`${
                                                    classes.fileDropZone
                                                  } ${
                                                    state.isDragActive
                                                      ? classes.fileDropZoneDrag
                                                      : ''
                                                  }`}
                                                >
                                                  {(() => {
                                                    if (state.isDragActive) {
                                                      return (
                                                        <h4>
                                                          Drop file&#40;s&#41;
                                                          here
                                                        </h4>
                                                      );
                                                    }

                                                    return (
                                                      <div className="text-center">
                                                        <h4>
                                                          Drop file&#40;s&#41;
                                                          here
                                                        </h4>
                                                        <p>
                                                          or click to select
                                                          file&#40;s&#41;
                                                        </p>
                                                      </div>
                                                    );
                                                  })()}
                                                </div>
                                                <input
                                                  type="file"
                                                  {...state.getInputProps()}
                                                />
                                              </div>
                                            );
                                          }}
                                        </Dropzone>
                                      );
                                    })()}
                                  </Grid>
                                ) : null}

                                {!!workItem.submission &&
                                  workItem.submission.file.alternateLink && (
                                    <Grid
                                      item
                                      container
                                      direction="column"
                                      xs={6}
                                    >
                                      <h6 className="mb-3">Submitted File:</h6>

                                      <Card>
                                        <CardContent className="p-3">
                                          <Grid
                                            container
                                            alignItems="center"
                                            spacing={2}
                                            direction="row"
                                          >
                                            <Grid item>
                                              <AttachmentIcon fontSize="large" />
                                            </Grid>
                                            <Grid item className="flex-fill">
                                              <Typography>
                                                {workItem.submission.file.title}
                                              </Typography>
                                            </Grid>
                                            <Grid item>
                                              {!!workItem.submission.file.id &&
                                                /\.zip/gi.test(
                                                  workItem.submission.file.title
                                                ) && (
                                                  <IconButton
                                                    className="ml-1"
                                                    onClick={() => {
                                                      if (workItem.submission)
                                                        openFileExplorer(
                                                          workItem.submission
                                                            .file.id
                                                        );
                                                    }}
                                                  >
                                                    <OpenInBrowser />
                                                  </IconButton>
                                                )}

                                              {!!workItem.submission.file
                                                .alternateLink && (
                                                <IconButton
                                                  className="ml-1"
                                                  component="a"
                                                  href={
                                                    workItem.submission.file
                                                      .alternateLink
                                                  }
                                                  target="_blank"
                                                  download={
                                                    workItem.submission.file
                                                      .title
                                                  }
                                                >
                                                  <GetApp />
                                                </IconButton>
                                              )}
                                            </Grid>
                                          </Grid>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )}
                              </Grid>

                              <Box pt={1}>
                                <Typography>
                                  <small>
                                    {isFileRejected ? (
                                      <span style={{ color: '#FF0033' }}>
                                        Unfortunately, we do not support zip
                                        files or any other compressed formats at
                                        this time. Please upload files that are
                                        not compressed or zipped.
                                      </span>
                                    ) : (
                                      <span>
                                        Please submit the necessary
                                        file&#40;s&#41; and we will zip it for
                                        you.
                                      </span>
                                    )}

                                    {/* <a
                                      href="#/"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        alert(
                                          <div className="mw-100 vw-100">
                                            <iframe
                                              title="Video"
                                              src="https://drive.google.com/file/d/1HI4HdUdvYcO9zre1iQt4u8Dd-gQ7VrUO/preview?t=3m20s"
                                              className="w-100"
                                              height="325"
                                              allow="autoplay"
                                              allowFullScreen={true}
                                            />
                                          </div>,
                                          {
                                            title: 'How to zip your files',
                                          }
                                        );
                                      }}
                                    >
                                      Don't know how to zip a file?
                                    </a> */}
                                  </small>
                                </Typography>
                              </Box>
                            </Box>
                          );

                        return (
                          <SpecTest
                            key={workItem.spec.id}
                            courseId={courseId}
                            courseWorkId={courseWorkId}
                            spec={workItem.spec}
                            answer={workItem.answer}
                            graded={!!grade}
                            onAnswerChange={(answer: string) => {
                              setWorkItems(
                                workItems.map((item) => {
                                  if (item === workItem) item.answer = answer;
                                  return item;
                                })
                              );
                            }}
                            onTestResultChange={(result: TestResult | null) => {
                              setWorkItems(
                                workItems.map((item) => {
                                  if (item === workItem) {
                                    if (item.uploadStatus)
                                      delete item.uploadStatus;
                                    item.testResult = result;
                                  }
                                  return item;
                                })
                              );
                            }}
                            onBeforeTest={async () =>
                              await validateWorkItemAnswer(workItem)
                            }
                            showReference={true}
                            resultFooter={(() => {
                              const { uploadStatus } = workItem;

                              if (uploadStatus?.errorMessage)
                                return (
                                  <p className="m-0" style={{ color: 'red' }}>
                                    <small>{uploadStatus.errorMessage}</small>
                                    <Button
                                      className="ml-2"
                                      variant="text"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setWorkItems(
                                          workItems.map((item) => {
                                            if (
                                              item === workItem &&
                                              item.uploadStatus
                                            )
                                              uploadStatus.errorMessage = '';
                                            return item;
                                          })
                                        );
                                      }}
                                    >
                                      close
                                    </Button>
                                  </p>
                                );

                              if (uploadStatus?.successMessage)
                                return (
                                  <p className="m-0" style={{ color: 'green' }}>
                                    <small>{uploadStatus.successMessage}</small>
                                  </p>
                                );

                              return (
                                <Button
                                  type="button"
                                  disabled={
                                    uploadStatus?.isLoading ||
                                    !workItem.answer ||
                                    workItem.answer ===
                                      workItem.submission?.content ||
                                    (workItem.spec.name.toUpperCase() ===
                                      'EXAM' &&
                                      hasExamEnded)
                                  }
                                  color="primary"
                                  variant="contained"
                                  startIcon={
                                    uploadStatus?.isLoading ? (
                                      <CircularProgress
                                        size="1em"
                                        color="inherit"
                                      />
                                    ) : (
                                      <SaveIcon />
                                    )
                                  }
                                  onClick={() => submitWorkItem(workItem)}
                                >
                                  <span>
                                    {workItem.spec.name.toUpperCase() ===
                                      'EXAM' && hasExamEnded
                                      ? 'Exam Ended'
                                      : 'Submit For Checking'}
                                  </span>
                                </Button>
                              );
                            })()}
                            editorFooter={
                              <ButtonGroup>
                                {renderSpecCommentsOpenButton({
                                  specId: workItem.spec.id,
                                })}
                                <Button
                                  startIcon={
                                    isFeedbackLoading[workItem.spec.id] ? (
                                      <CircularProgress
                                        size="1em"
                                        color="inherit"
                                      />
                                    ) : (
                                      <FeedbackIcon />
                                    )
                                  }
                                  onClick={() =>
                                    openFeedbackForm(workItem.spec)
                                  }
                                  color="primary"
                                  size="small"
                                >
                                  Feedback
                                </Button>
                              </ButtonGroup>
                            }
                          />
                        );
                      })()}
                    </div>
                  </Paper>
                ))}
              </>
            );
        })()}
      </div>
    </StudentLayout>
  );
};

export default AssignmentProfile;
